import DragAndDrop from '@components/Common/DragAndDrop.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import { UIDiv, UIInput, UIText } from '@ui';

export default class TaskCreateDialog extends Modal {
  constructor({
    projectName = '',
    sentence = i18next.t('pages.projects.uploadTask') + projectName !== ''
      ? projectName
      : fileName,
    noBg = false,
    location = 'navbar',
  }) {
    super(sentence, { noBg });
    this.isLoading = false;
    this.location = location;
    this.resolve = null;
    this.selectedFiles = new Map();
    this.previewElementsMap = new Map();

    // Add paste event listener
    this.pasteHandler = this.handlePaste.bind(this);
    document.addEventListener('paste', this.pasteHandler);
  }

  handlePaste(event) {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      this.fileInputContainer.handleFileSelection(files);
    }
  }

  createModal() {
    const modal = super.createModal();
    const formContainer = new UIDiv().setClass('space-y-4 p-1 w-full');
    this.cancelButton.setClass('hidden');

    // --- Task Name ---
    const taskNameLabel = new UIText(
      i18next.t('pages.projects.taskName'),
    ).setClass('block text-sm font-medium text-gray-700 mb-1');
    this.inputField = new UIInput('')
      .setPlaceholder(i18next.t('pages.projects.taskName'))
      .setClass('w-full p-2 border border-gray-300 rounded-md');

    this.inputField.onKeyDown(e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.onAction();
      }
    });

    requestAnimationFrame(() => {
      this.inputField.dom.focus();
    });

    const taskNameContainer = new UIDiv().setClass('flex flex-col');
    taskNameContainer.add(taskNameLabel, this.inputField);

    // --- Upload Image ---
    const uploadLabel = new UIText('Upload Files').setClass(
      'block text-sm font-medium text-gray-700 mb-1',
    );

    // File upload container (drag & drop area)
    // Now also holds the selected file previews
    this.fileInputContainer = new DragAndDrop({
      callBack: selectedFiles => (this.selectedFiles = selectedFiles),
      allowVideoFrameUpload: this.location === 'cadViewer',
    });

    // Wrap label and file container
    const uploadContainer = new UIDiv().setClass('flex flex-col');
    uploadContainer.add(uploadLabel, this.fileInputContainer);

    // Add all sections to the form container
    if (this.location === 'navbar') formContainer.add(taskNameContainer);

    formContainer.add(uploadContainer);
    this.contentContainer.add(formContainer);

    // --- Bottom Buttons ---
    const buttonBar = new UIDiv().setClass(
      'flex items-center justify-center mt-6 space-x-3 flex-wrap gap-2',
    );

    // The real "Add task" button (actionButton) is inherited from Modal
    this.actionButton
      .setInnerText(i18next.t('pages.projects.addTask'))
      .setClass(
        'px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700',
      );

    buttonBar.add(this.actionButton);
    this.contentContainer.add(buttonBar);

    return modal;
  }

  async onAction() {
    if (this.selectedFiles.size === 0) {
      // No files selected and no task name provided
      toast.error('Please provide a task name or select files to upload.');
      return;
    }
    if (this.isLoading) return;
    const taskName = this.inputField.getValue().trim();
    const files = Array.from(this.selectedFiles.values()).map(fileData => ({
      file: fileData.file,
      isPanorama: fileData.isPanorama,
      isDetected: fileData.isDetection,
      detectedPosition: fileData.detectedPosition,
      detectionResponse: fileData.detectionResponse,
      useVirtualTexture: fileData.useVirtualTexture,
      uploadAsFrames: fileData.uploadAsFrames,
    }));

    if (taskName || files.length) {
      if (this.resolve) {
        this.resolve({ name: taskName, files });
      }
    }
  }

  show(elemId) {
    return new Promise(resolve => {
      this.resolve = resolve;
      super.show(elemId);
    });
  }

  handleError(error) {
    console.error('Error occurred:', error);
    this.setLoading(false);
    const errorMsg = new UIDiv()
      .setClass('text-red-500 mt-2')
      .setInnerText(`An error occurred: ${error}`);
    this.contentContainer.add(errorMsg);
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.actionButton.setDisabled(loading);
    this.actionButton.setInnerText(
      loading
        ? i18next.t('pages.projects.uploading')
        : i18next.t('pages.projects.addTask'),
    );
  }

  close() {
    // Remove paste event listener when dialog is closed
    document.removeEventListener('paste', this.pasteHandler);

    // Always allow closing—even during upload—and clean up object URLs
    for (const [, fileData] of this.selectedFiles) {
      if (fileData.file.type.startsWith('image/')) {
        URL.revokeObjectURL(fileData.file);
      }
    }

    // Call any cleanup handlers
    if (this.onClose) this.onClose();

    super.close();
  }
}
